import React, { FC, forwardRef, useState } from 'react';
import clsx from 'clsx';
import ReactDatePicker from 'react-datepicker';
import { CalendarIcon } from '@heroicons/react/outline';
import { format } from 'date-fns';

interface ITableDatePicker {
  isDateRange?: boolean;
  onChange: (date: any) => void;
  isLoading?: boolean;
}

const TableDatePicker: FC<ITableDatePicker> = ({ isDateRange, onChange, isLoading }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filter, setFilter] = useState<'All' | 'Today' | 'na'>('All');

  const onChangeDate = (date: any) => {
    if (Array.isArray(date)) {
      const [start, end] = date;
      setStartDate(start);
      setEndDate(end);
      onChange([start, end]);
    } else {
      setStartDate(date);
    }

    setFilter('na');
  };

  const handleClearDates = () => {
    if (isDateRange) {
      setStartDate(null);
      setEndDate(null);
      onChange([null, null]);
    } else {
      setStartDate(null);
      onChange(null);
    }

    setFilter('All');
  };

  const handleToday = () => {
    const today = new Date();
    setStartDate(today);
    setEndDate(today);
    onChange([today, today]);
    setFilter('Today');
  };

  const CalendarCustomInput = forwardRef(({ onClick }: any, _ref: any) => (
    <div
      className={clsx(
        'py-1 px-3 rounded-full text-xs font-medium cursor-pointer transition-colors select-none flex items-center gap-1.5',
        filter === 'na' ? 'bg-hazel-yellow-100 text-gray-900' : 'bg-white text-gray-500 font-light hover:bg-gray-50 border border-gray-200'
      )}
      onClick={onClick}
    >
      <CalendarIcon className="h-4 w-4" />
      {startDate && endDate && filter === 'na' && (
        <span>{`${format(new Date(startDate), 'dd.MM.yyyy')} - ${format(new Date(endDate), 'dd.MM.yyyy')}`}</span>
      )}
    </div>
  ));

  const pillActive = 'bg-hazel-yellow-100 text-gray-900 font-medium';
  const pillInactive = 'bg-white text-gray-500 font-light hover:bg-gray-50 border border-gray-200';
  const pillBase = 'py-1 px-4 rounded-full text-xs cursor-pointer transition-colors select-none';

  return (
    <div className="flex items-center gap-2">
      <div className={clsx(pillBase, filter === 'All' ? pillActive : pillInactive)} onClick={handleClearDates}>
        All
      </div>

      <div className={clsx(pillBase, filter === 'Today' ? pillActive : pillInactive)} onClick={handleToday}>
        Today
      </div>

      <ReactDatePicker
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        onChange={onChangeDate}
        dateFormat="dd/MM/yyyy"
        selectsRange={isDateRange}
        placeholderText="Select date range"
        customInput={<CalendarCustomInput />}
        popperProps={{ strategy: 'fixed' }}
        popperClassName="!z-50"
      />
    </div>
  );
};

export default TableDatePicker;
